import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useContext, useEffect, useState } from "react";
import useColors from "../../../../../api/hooks/useColors";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import TrendIndicator from "../../TrendIndicator";

const SecurityScoreChart = ({
  mainTimePoint,
  baseTimePoint,
  heatmapCoverageByTimepoints = null,
}) => {
  const colors = useColors();
  const [score, setScore] = useState(0);
  const [trend, setTrend] = useState(null);
  const { heatmapDisplayMode } = useContext(CustomerFeatureFlagsContext);

  useEffect(() => {
    if (
      !heatmapCoverageByTimepoints ||
      !heatmapCoverageByTimepoints[mainTimePoint]
    ) {
      return;
    }

    const currentCoverage =
      heatmapCoverageByTimepoints[mainTimePoint][heatmapDisplayMode];

    if (!currentCoverage) {
      return;
    }

    setScore(currentCoverage.toFixed(1));

    if (
      baseTimePoint !== mainTimePoint &&
      heatmapCoverageByTimepoints[baseTimePoint]
    ) {
      const previousCoverage =
        heatmapCoverageByTimepoints[baseTimePoint][heatmapDisplayMode];

      if (previousCoverage) {
        const diff = currentCoverage - previousCoverage;
        const percentChange =
          previousCoverage > 0
            ? ((diff / previousCoverage) * 100).toFixed(1)
            : 0;

        setTrend({
          diff: Math.abs(diff).toFixed(1),
          isPositive: diff >= 0,
          percentChange: Math.abs(percentChange),
        });
      } else {
        setTrend(null);
      }
    } else {
      setTrend(null);
    }
  }, [
    mainTimePoint,
    baseTimePoint,
    heatmapCoverageByTimepoints,
    heatmapDisplayMode,
  ]);

  const chartData = [
    {
      id: "Security Score",
      value: score,
      label: "Security Score",
      color: colors.primary_300main,
    },
    // Add remaining portion to complete the circle
    {
      id: "Remaining",
      value: 100 - score,
      label: "Remaining",
      color: colors.grey_200,
    },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        height: 220,
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          filter: "drop-shadow(0px 0px 6px rgba(0, 0, 0, 0.16))",
        }}
      >
        <PieChart
          series={[
            {
              data: chartData,
              innerRadius: 90,
              outerRadius: 110,
              paddingAngle: 0.5,
              startAngle: -90,
              endAngle: 270,
              cornerRadius: 4,
            },
          ]}
          slotProps={{
            legend: {
              hidden: true,
            },
            tooltip: {
              trigger: "item",
              itemContent: (params) => {
                if (params?.itemData?.id === "Remaining") return null;
                return (
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2">
                      {params?.itemData?.label}: {params?.itemData?.value}%
                    </Typography>
                  </Box>
                );
              },
            },
          }}
          sx={{
            "& .MuiPieArc-root": {
              stroke: colors.grey_300,
              strokeWidth: 1,
            },
          }}
          height={250}
          width={250}
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          width: "100%",
          maxWidth: 140,
          maxHeight: 140,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {" "}
        {/* Trend indicator */}
        {trend && <TrendIndicator data={trend} showPercent={true} />}
        {/* Score percentage */}
        <Typography variant="h3">{score}%</Typography>
      </Box>
    </Box>
  );
};

export default SecurityScoreChart;
